/**
 * Éditeur Notion-like d'un exercice V2.
 *
 * UX :
 * - Chaque bloc est affiché en mode CONSULTATION par défaut (BlockCard) :
 *   badge type + titre + aperçu + actions (▲ ▼ Éditer ⎘ ✕).
 * - Cliquer « Éditer » bascule le bloc en mode ÉDITION (BlockShell), avec
 *   le formulaire complet + bouton « ✓ Terminé » pour revenir en consultation.
 * - Les blocs énoncé et corrigé ne sont pas séparés en sections : tout est
 *   rendu dans un flux unifié, organisé autour des `statement_question`.
 * - Les blocs enfants (corrigés/indices d'une question, distracteurs d'une
 *   correction QCM) sont masqués par défaut et révélés via des pills toggle
 *   sur le parent : « n corrigés », « n indices », « n distracteurs ». Quand
 *   on les ouvre, ils s'affichent juste après le parent, indentés.
 *
 * Mutations : useBlocksMutate → edge fn exercise-blocks-mutate (batch atomique).
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileText,
  Info,
  Lightbulb,
  Search,
  Settings,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import type { Database } from '@jeraisonne/db';
import { SUBJECTS, SCHOOL_LEVELS } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';
import { usePublishedChapters } from '@/lib/admin/exercise-queries';
import { ExerciseThemesPicker } from '@/components/exercise-themes-picker';
import { IllustrationEditor } from '@/components/illustration-editor';
import {
  exerciseV2QueryKey,
  nextClientId,
  useBlocksMutate,
  useExerciseV2,
} from '@/lib/exercise-v2/api';
import type {
  BlockMutationOp,
  ExerciseBlock,
  ExerciseBlockSection,
  ExerciseBlockType,
  ExerciseV2Meta,
  IncomingLessonLink,
} from '@/lib/exercise-v2/types';
import {
  blocksInSection,
  buildIsolateVisualOps,
  buildDeleteSubtreeOps,
  childrenOf,
  positionAfter,
  positionAtEnd,
  positionBefore,
  listQuestions,
} from '@/lib/exercise-v2/block-helpers';
import { BlockShell } from './BlockShell';
import { BlockCard, type ChildToggle } from './BlockCard';
import { BlockVoiceTools } from './BlockVoiceTools';
import { V2GenerationPanel } from './V2GenerationPanel';
import {
  QuestionScratchTools,
  QuestionSheetBadge,
  ScratchSheetsPanel,
} from '@/features/scratch-editor';

export interface ExerciseEditorV2Props {
  exerciseId: string;
}

const DEFAULT_DATA_FOR: Record<ExerciseBlockType, Record<string, unknown>> = {
  statement: { content_md: 'Nouveau contexte…' },
  statement_question: { prompt_md: 'Nouvelle question…' },
  correction: { explanation_md: 'Nouvelle étape de corrigé…' },
  distractor: { label_md: 'Mauvaise réponse' },
  explanation: { body_md: 'Explication libre…' },
  hint: { body_md: 'Indice…' },
  note: { body_md: 'Note tuteur…' },
  clue_hunt: {
    intro_md: "Trouve les indices dans l'énoncé",
    timeout_ms: 30000,
    clues: [],
  },
};

export function ExerciseEditorV2({ exerciseId }: ExerciseEditorV2Props) {
  const query = useExerciseV2(exerciseId);
  const mutate = useBlocksMutate(exerciseId);

  if (query.isPending) {
    return <div className="p-6 text-center text-sm text-slate-500">Chargement de l'exercice…</div>;
  }
  if (query.isError) {
    return (
      <div className="p-6 text-center text-sm text-red-600">
        Erreur de chargement. <button onClick={() => query.refetch()}>Réessayer</button>
      </div>
    );
  }
  const payload = query.data!;

  return (
    <>
      <ExerciseSettingsPanel exerciseId={exerciseId} meta={payload.meta} />
      {payload.meta.is_parametric === true && (
        <div className="mx-auto mb-3 max-w-3xl rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <strong className="font-semibold">Exercice paramétré.</strong> Le texte ci-dessous est le
          rendu <em>canonique</em> — chaque élève joue ses propres valeurs, tirées à sa tentative.
          Édite la spec depuis son générateur puis reconstruis les blocs : une modification manuelle
          ici serait recouverte par le calque de l&apos;instance.
        </div>
      )}
      <V2GenerationPanel exerciseId={exerciseId} blocks={payload.blocks} />
      {payload.meta.is_parametric !== true && (
        <ScratchSheetsPanel exerciseId={exerciseId} blocks={payload.blocks} />
      )}
      <V2EditorBody
        exerciseId={exerciseId}
        blocks={payload.blocks}
        lessonLinks={payload.lesson_links ?? []}
        isParametric={payload.meta.is_parametric === true}
        isMutating={mutate.isPending}
        mutationError={mutate.isError ? (mutate.error as Error) : null}
        onApply={(ops) => mutate.mutate(ops)}
        onResetError={() => mutate.reset()}
      />
    </>
  );
}

const SUBJECT_LABEL = Object.fromEntries(SUBJECTS.map((s) => [s.value, s.label]));
const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));

const SELECT_CLS =
  'h-11 w-full appearance-none rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100';
const INPUT_CLS =
  'h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100';
const FIELD_LABEL_CLS = 'mb-1 block text-[12px] font-semibold text-slate-700';

/**
 * Panneau « Paramètres » de l'exercice V2 : édite les champs de la table
 * `exercises` (hors blocs), absents de l'edge fn de blocs. Les sélecteurs sont
 * sauvegardés au changement, les champs texte/nombre au blur — cohérent avec le
 * modèle de sauvegarde immédiate de la V2. Après écriture, on invalide le
 * payload V2 (meta) + les caches lecteurs (détail, flag calculatrice).
 */
function ExerciseSettingsPanel({ exerciseId, meta }: { exerciseId: string; meta: ExerciseV2Meta }) {
  const qc = useQueryClient();
  const { data: chapters } = usePublishedChapters();
  const [open, setOpen] = useState(false);

  const [title, setTitle] = useState(meta.title);
  const [estMinutes, setEstMinutes] = useState(
    meta.estimated_minutes != null ? String(meta.estimated_minutes) : '',
  );

  const { data: requiresCalculator = false } = useQuery({
    queryKey: ['exercise-requires-calculator', exerciseId],
    enabled: !!exerciseId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<boolean> => {
      const { data } = await supabase
        .from('exercises')
        .select('requires_calculator')
        .eq('id', exerciseId)
        .maybeSingle();
      return (data as { requires_calculator?: boolean } | null)?.requires_calculator ?? false;
    },
  });

  const update = useMutation({
    mutationFn: async (patch: Database['public']['Tables']['exercises']['Update']) => {
      const { error } = await supabase.from('exercises').update(patch).eq('id', exerciseId);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: exerciseV2QueryKey(exerciseId) });
      qc.invalidateQueries({ queryKey: ['exercise-detail', exerciseId] });
      qc.invalidateQueries({ queryKey: ['exercise-requires-calculator', exerciseId] });
    },
  });

  // Exercice issu d'une épreuve (extraction PDF) : on récupère l'épreuve pour
  // afficher son titre + sa matière et son niveau de manière disjointe (un exo
  // d'épreuve n'a en général pas de chapitre rattaché).
  const { data: epreuve } = useQuery({
    queryKey: ['exercise-source-epreuve', meta.source_epreuve_id],
    enabled: !!meta.source_epreuve_id,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from('epreuves')
        .select('id, title, level, subject')
        .eq('id', meta.source_epreuve_id!)
        .maybeSingle();
      return data as { id: string; title: string; level: string; subject: string | null } | null;
    },
  });

  const currentChapter = chapters?.find((c) => c.id === meta.chapter_id) ?? null;

  return (
    <div className="mx-auto max-w-4xl px-3 pt-4 sm:px-6">
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center justify-between px-4 py-3 hover:bg-slate-50"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Settings className="h-4 w-4 text-slate-500" />
            Paramètres de l'exercice
          </span>
          {open ? (
            <ChevronUp className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-400" />
          )}
        </button>

        {open && (
          <div className="space-y-4 border-t border-slate-100 px-4 py-4">
            {update.isError && (
              <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {(update.error as Error).message}
              </p>
            )}

            {epreuve && (
              <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-3">
                <a
                  href={`/tutor/admin/epreuves/${epreuve.id}`}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-800 hover:underline"
                >
                  <FileText className="h-4 w-4 shrink-0 text-indigo-500" />
                  <span>Épreuve : {epreuve.title}</span>
                </a>
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-0.5 text-xs text-indigo-700 ring-1 ring-indigo-200">
                    <span className="text-indigo-400">Matière</span>
                    <span className="font-medium">
                      {epreuve.subject ? (SUBJECT_LABEL[epreuve.subject] ?? epreuve.subject) : '—'}
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-0.5 text-xs text-indigo-700 ring-1 ring-indigo-200">
                    <span className="text-indigo-400">Niveau</span>
                    <span className="font-medium">
                      {LEVEL_LABEL[epreuve.level] ?? epreuve.level}
                    </span>
                  </span>
                </div>
              </div>
            )}

            <div>
              <label className={FIELD_LABEL_CLS}>Titre</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => {
                  const trimmed = title.trim();
                  if (trimmed && trimmed !== meta.title) update.mutate({ title: trimmed });
                }}
                className={INPUT_CLS}
              />
            </div>

            <div>
              <label className={FIELD_LABEL_CLS}>Chapitre (matière · niveau)</label>
              <select
                value={meta.chapter_id ?? ''}
                onChange={(e) => {
                  if (e.target.value) update.mutate({ chapter_id: e.target.value });
                }}
                className={SELECT_CLS}
              >
                <option value="">— Aucun chapitre —</option>
                {(chapters ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className={FIELD_LABEL_CLS}>Difficulté</label>
                <select
                  value={meta.difficulty}
                  onChange={(e) =>
                    update.mutate({ difficulty: e.target.value as ExerciseV2Meta['difficulty'] })
                  }
                  className={SELECT_CLS}
                >
                  <option value="easy">Facile</option>
                  <option value="medium">Moyen</option>
                  <option value="hard">Difficile</option>
                </select>
              </div>

              <div>
                <label className={FIELD_LABEL_CLS}>Durée estimée (min)</label>
                <input
                  type="number"
                  min={1}
                  inputMode="numeric"
                  value={estMinutes}
                  onChange={(e) => setEstMinutes(e.target.value)}
                  onBlur={() => {
                    const n = parseInt(estMinutes, 10);
                    if (Number.isFinite(n) && n > 0 && n !== meta.estimated_minutes) {
                      update.mutate({ estimated_minutes: n });
                    }
                  }}
                  className={INPUT_CLS}
                />
              </div>

              <div>
                <label className={FIELD_LABEL_CLS}>Statut</label>
                <select
                  value={meta.status}
                  onChange={(e) =>
                    update.mutate({ status: e.target.value as ExerciseV2Meta['status'] })
                  }
                  className={SELECT_CLS}
                >
                  <option value="draft">Brouillon</option>
                  <option value="published">Publié</option>
                  <option value="archived">Archivé</option>
                  <option value="awaiting_review">En relecture</option>
                </select>
              </div>
            </div>

            <label className="flex min-h-11 cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                checked={requiresCalculator}
                onChange={(e) => update.mutate({ requires_calculator: e.target.checked })}
                disabled={update.isPending}
                className="text-primary-600 focus:ring-primary-400 h-4 w-4 rounded"
              />
              <span className="text-sm">
                <span className="font-semibold text-slate-800">Calculatrice requise</span>
                <span className="ml-1.5 text-slate-500">
                  — affiche une calculatrice intégrée pendant l'exercice
                </span>
              </span>
            </label>

            <label className="flex min-h-11 cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                checked={meta.is_diagnostic ?? false}
                onChange={(e) =>
                  update.mutate({
                    is_diagnostic: e.target.checked,
                  } as unknown as Database['public']['Tables']['exercises']['Update'])
                }
                disabled={update.isPending}
                className="text-primary-600 focus:ring-primary-400 h-4 w-4 rounded"
              />
              <span className="text-sm">
                <span className="font-semibold text-slate-800">Exercice diagnostic</span>
                <span className="ml-1.5 text-slate-500">
                  — QCM de cours, toujours gratuit, listé par l'objectif Diagnostic du niveau
                </span>
              </span>
            </label>

            <div className="border-t border-slate-100 pt-4">
              <ExerciseThemesPicker exerciseId={exerciseId} />
            </div>

            <div className="border-t border-slate-100 pt-4">
              <IllustrationEditor
                entityType="exercise"
                entityId={exerciseId}
                initialUrl={meta.illustration_url}
                initialPrompt={meta.illustration_prompt}
                subject={currentChapter?.subject}
                level={currentChapter?.level}
                title={meta.title}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function V2EditorBody({
  exerciseId,
  blocks,
  lessonLinks,
  isMutating,
  mutationError,
  isParametric = false,
  onApply,
  onResetError,
}: {
  exerciseId: string;
  blocks: ExerciseBlock[];
  lessonLinks: IncomingLessonLink[];
  isMutating: boolean;
  mutationError: Error | null;
  /** Exercice paramétrique d'un Blueprint : ce qu'on édite ici n'est que le rendu canonique. */
  isParametric?: boolean;
  onApply: (ops: BlockMutationOp[]) => void;
  onResetError: () => void;
}) {
  const lessonLinkCountByBlock = useMemo(() => {
    const map = new Map<string, number>();
    for (const l of lessonLinks) {
      map.set(l.exercise_block_id, (map.get(l.exercise_block_id) ?? 0) + 1);
    }
    return map;
  }, [lessonLinks]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [showMeta, setShowMeta] = useState(false);

  const isExpanded = (key: string) => expanded[key] === true;
  const toggleExpanded = (key: string) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  const ensureExpanded = (key: string) =>
    setExpanded((prev) => (prev[key] ? prev : { ...prev, [key]: true }));

  // ── Index des blocs ──────────────────────────────────────────────────────
  const statementBlocks = useMemo(() => blocksInSection(blocks, 'statement'), [blocks]);
  const correctionBlocks = useMemo(() => blocksInSection(blocks, 'correction'), [blocks]);
  const metaBlocks = useMemo(() => blocksInSection(blocks, 'meta'), [blocks]);

  const questions = useMemo(() => listQuestions(blocks), [blocks]);
  const corrections = useMemo(
    () =>
      blocks.filter((b) => b.block_type === 'correction').sort((a, b) => a.position - b.position),
    [blocks],
  );

  const correctionsByParent = useMemo(() => {
    const map = new Map<string, ExerciseBlock[]>();
    for (const c of corrections) {
      if (!c.parent_id) continue;
      const arr = map.get(c.parent_id) ?? [];
      arr.push(c);
      map.set(c.parent_id, arr);
    }
    return map;
  }, [corrections]);

  const hintsByParent = useMemo(() => {
    const map = new Map<string, ExerciseBlock[]>();
    for (const b of blocks) {
      if (b.block_type !== 'hint' || !b.parent_id) continue;
      const arr = map.get(b.parent_id) ?? [];
      arr.push(b);
      map.set(b.parent_id, arr);
    }
    map.forEach((arr) => arr.sort((a, b) => a.position - b.position));
    return map;
  }, [blocks]);

  const distractorsByParent = useMemo(() => {
    const map = new Map<string, ExerciseBlock[]>();
    for (const b of blocks) {
      if (b.block_type !== 'distractor' || !b.parent_id) continue;
      const arr = map.get(b.parent_id) ?? [];
      arr.push(b);
      map.set(b.parent_id, arr);
    }
    map.forEach((arr) => arr.sort((a, b) => a.position - b.position));
    return map;
  }, [blocks]);

  const explanationsByParent = useMemo(() => {
    const map = new Map<string, ExerciseBlock[]>();
    for (const b of blocks) {
      if (b.block_type !== 'explanation' || !b.parent_id) continue;
      const arr = map.get(b.parent_id) ?? [];
      arr.push(b);
      map.set(b.parent_id, arr);
    }
    map.forEach((arr) => arr.sort((a, b) => a.position - b.position));
    return map;
  }, [blocks]);

  const clueHuntsByParent = useMemo(() => {
    const map = new Map<string, ExerciseBlock[]>();
    for (const b of blocks) {
      if (b.block_type !== 'clue_hunt' || !b.parent_id) continue;
      const arr = map.get(b.parent_id) ?? [];
      arr.push(b);
      map.set(b.parent_id, arr);
    }
    map.forEach((arr) => arr.sort((a, b) => a.position - b.position));
    return map;
  }, [blocks]);

  const orphanCorrections = useMemo(
    () => correctionBlocks.filter((c) => c.block_type === 'correction' && c.parent_id === null),
    [correctionBlocks],
  );
  const orphanExplanations = useMemo(
    () => blocks.filter((b) => b.block_type === 'explanation' && b.parent_id === null),
    [blocks],
  );

  // ── Op builders ──────────────────────────────────────────────────────────

  function moveBlock(block: ExerciseBlock, direction: 'up' | 'down') {
    const section = blocksInSection(blocks, block.section);
    const idx = section.findIndex((b) => b.id === block.id);
    if (idx < 0) return;
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= section.length) return;
    // On ne fait PAS un swap (2 reorder qui se croisent et violent l'unique
    // (section, position)). On pose une position fractionnaire qui s'intercale
    // sans collision : juste avant `targetIdx` si on monte, juste après sinon.
    const newPos =
      direction === 'up' ? positionBefore(section, targetIdx) : positionAfter(section, targetIdx);
    onApply([{ op: 'reorder', id: block.id, position: newPos }]);
  }

  function insertBlock(
    after: ExerciseBlock | null,
    section: ExerciseBlockSection,
    type: ExerciseBlockType,
    parentIdOverride?: string | null,
  ) {
    const sectionList = blocksInSection(blocks, section);
    const insertIdx = after ? sectionList.findIndex((b) => b.id === after.id) : -1;
    const newPos = after ? positionAfter(sectionList, insertIdx) : positionAtEnd(sectionList);

    let parent_id: string | null = parentIdOverride ?? null;
    if (parentIdOverride === undefined) {
      if (type === 'hint' || (type === 'correction' && questions.length > 0)) {
        parent_id = questions[0]?.id ?? null;
      }
      if (type === 'distractor') {
        const checkpoints = corrections.filter((c) => c.parent_id !== null);
        parent_id = checkpoints[0]?.id ?? null;
      }
      // clue_hunt : parent_id RESTE null par défaut (bloc racine). Si un tuteur
      // veut le rattacher à une question, il passera par le sélecteur d'éditeur.
    }

    if ((type === 'hint' || type === 'distractor') && parent_id === null) {
      alert(
        type === 'hint'
          ? "Un indice doit être rattaché à une question. Crée d'abord une question."
          : 'Un distracteur doit être rattaché à une correction QCM.',
      );
      return;
    }

    onApply([
      {
        op: 'insert',
        client_id: nextClientId(type),
        block_type: type,
        section,
        parent_id,
        position: newPos,
        data: DEFAULT_DATA_FOR[type],
      },
    ]);
  }

  function duplicateBlock(block: ExerciseBlock) {
    const sectionList = blocksInSection(blocks, block.section);
    const idx = sectionList.findIndex((b) => b.id === block.id);
    const newPos = positionAfter(sectionList, idx);
    onApply([
      {
        op: 'insert',
        client_id: nextClientId('dup'),
        block_type: block.block_type,
        section: block.section,
        parent_id: block.parent_id,
        position: newPos,
        data: structuredClone(block.data),
      },
    ]);
  }

  function deleteBlock(block: ExerciseBlock) {
    // Suppression EN CASCADE : un corrigé entraîne ses distracteurs / explications
    // enfants (sinon ils deviennent orphelins → la FK les délie et le trigger de
    // topologie refuse la suppression).
    const childCount = childrenOf(blocks, block.id).length;
    const message =
      childCount > 0
        ? `Supprimer ce bloc ${block.block_type} et ses ${childCount} bloc(s) enfant(s) ?`
        : `Supprimer ce bloc ${block.block_type} ?`;
    if (!confirm(message)) return;
    if (editingId === block.id) setEditingId(null);
    onApply(buildDeleteSubtreeOps(blocks, block.id));
  }

  function patchData(block: ExerciseBlock, data: Record<string, unknown>) {
    onApply([{ op: 'update', id: block.id, patch: { data } }]);
  }

  function patchParent(block: ExerciseBlock, parent_id: string | null) {
    onApply([{ op: 'update', id: block.id, patch: { parent_id } }]);
  }

  // Isole un visuel : le détache de son bloc et le pose SEUL dans un nouveau
  // bloc énoncé (statement) inséré juste après.
  function isolateVisual(block: ExerciseBlock, visualId: string) {
    const ops = buildIsolateVisualOps(blocks, block, visualId, nextClientId('isolate-visual'));
    if (ops.length === 0) return;
    onApply(ops);
  }

  function splitBlockSelection(block: ExerciseBlock, selStart: number, selEnd: number) {
    const textField = primaryTextField(block.block_type);
    if (!textField) return;
    const data = block.data as Record<string, unknown>;
    const text = data[textField];
    if (typeof text !== 'string') return;

    const start = Math.max(0, Math.min(selStart, selEnd, text.length));
    const end = Math.max(0, Math.min(Math.max(selStart, selEnd), text.length));
    if (end <= start) return;

    const before = text.slice(0, start);
    const middle = text.slice(start, end);
    const after = text.slice(end);

    if (middle.trim() === '') {
      alert('La sélection est vide. Sélectionnez du texte non blanc pour découper.');
      return;
    }

    const hasBefore = before.trim() !== '';
    const hasAfter = after.trim() !== '';

    // Aucun "avant" ni "après" non vide → la sélection couvre tout le bloc, rien à faire.
    if (!hasBefore && !hasAfter) {
      alert("La sélection couvre l'intégralité du bloc — rien à découper.");
      return;
    }

    const sectionList = blocksInSection(blocks, block.section);
    const idx = sectionList.findIndex((b) => b.id === block.id);

    // makeData : pour l'update du bloc d'origine — on conserve toutes ses méta
    //   (title, visuals, etc.) et on remplace seulement le champ texte.
    const makeData = (text: string) => ({ ...data, [textField]: text });

    // ── Cas spécial : statement_question ────────────────────────────────────
    // On garde l'id (et donc les enfants corrigés/indices reliés via parent_id)
    // sur le DERNIER morceau. Les morceaux précédents deviennent de simples
    // blocs `statement` (énoncé/contexte) insérés AVANT l'original.
    if (block.block_type === 'statement_question') {
      const prev = sectionList[idx - 1];
      const prevPos = prev ? prev.position : 0;

      // Ordonner les parts non vides dans l'ordre du texte d'origine.
      const parts: string[] = [];
      if (hasBefore) parts.push(before);
      parts.push(middle);
      if (hasAfter) parts.push(after);

      const lastText = parts[parts.length - 1]!;
      const earlierParts = parts.slice(0, -1);

      const ops: BlockMutationOp[] = [];

      // Original (toujours statement_question) : reçoit la dernière partie.
      ops.push({ op: 'update', id: block.id, patch: { data: makeData(lastText) } });

      // Parts précédentes → nouveaux blocs `statement` insérés entre prev et l'original.
      // n parts ⇒ n+1 segments d'espacement entre prevPos et block.position.
      const gap = block.position - prevPos;
      const step = gap / (earlierParts.length + 1);
      earlierParts.forEach((partText, i) => {
        const pos = prevPos + step * (i + 1);
        ops.push({
          op: 'insert',
          client_id: nextClientId(`split-stmt-${i}`),
          block_type: 'statement',
          section: 'statement',
          parent_id: null, // statement DOIT avoir parent_id null (trigger SQL)
          position: pos,
          data: { content_md: partText },
        });
      });

      onApply(ops);
      return;
    }

    // ── Cas général : original reste au début, nouveaux blocs insérés APRÈS ─
    const next = sectionList[idx + 1];
    const nextPos = next ? next.position : block.position + 2;

    const ops: BlockMutationOp[] = [];
    // makeChildData : pour les NOUVEAUX blocs issus du split — on retire les
    //   méta qui ne doivent appartenir qu'au premier bloc (titre, visuels).
    const makeChildData = (text: string) => {
      const {
        title: _title,
        visuals: _visuals,
        ...rest
      } = data as {
        title?: unknown;
        visuals?: unknown;
      } & Record<string, unknown>;
      return { ...rest, [textField]: text };
    };
    const insertChild = (text: string, position: number, tag: string) => ({
      op: 'insert' as const,
      client_id: nextClientId(tag),
      block_type: block.block_type,
      section: block.section,
      parent_id: block.parent_id,
      position,
      data: makeChildData(text),
    });

    if (hasBefore && hasAfter) {
      const middlePos = (block.position + nextPos) / 2;
      const afterPos = (middlePos + nextPos) / 2;
      ops.push({ op: 'update', id: block.id, patch: { data: makeData(before) } });
      ops.push(insertChild(middle, middlePos, 'split-mid'));
      ops.push(insertChild(after, afterPos, 'split-after'));
    } else if (hasBefore && !hasAfter) {
      const middlePos = (block.position + nextPos) / 2;
      ops.push({ op: 'update', id: block.id, patch: { data: makeData(before) } });
      ops.push(insertChild(middle, middlePos, 'split-mid'));
    } else {
      // !hasBefore && hasAfter
      const afterPos = (block.position + nextPos) / 2;
      ops.push({ op: 'update', id: block.id, patch: { data: makeData(middle) } });
      ops.push(insertChild(after, afterPos, 'split-after'));
    }

    onApply(ops);
  }

  function mergeWithPrev(block: ExerciseBlock) {
    const sectionList = blocksInSection(blocks, block.section);
    const idx = sectionList.findIndex((b) => b.id === block.id);
    if (idx <= 0) return;
    const prev = sectionList[idx - 1]!;
    if (prev.block_type !== block.block_type || prev.parent_id !== block.parent_id) {
      alert(
        'Fusion impossible : le bloc précédent doit être du même type et avoir le même parent.',
      );
      return;
    }
    const textField = primaryTextField(block.block_type);
    if (!textField) return;
    onApply([{ op: 'merge', into_id: prev.id, from_id: block.id, text_field: textField }]);
  }

  // ── Rendu d'un bloc (view ↔ edit) ────────────────────────────────────────

  function makeToggle(
    parent: ExerciseBlock,
    kind: 'corrections' | 'distractors' | 'hints' | 'explanations' | 'clue_hunts',
    ariaLabelFor: (n: number) => string,
    Icon: LucideIcon,
    children: ExerciseBlock[],
    childType: ExerciseBlockType,
  ): ChildToggle {
    const key = `${parent.id}:${kind}`;
    // Section du nouvel enfant : hint toujours dans 'correction', explanation
    // suit la section du parent, autres types implicites.
    const childSection: ExerciseBlockSection =
      childType === 'hint'
        ? 'correction'
        : childType === 'explanation'
          ? parent.section === 'meta'
            ? 'correction'
            : parent.section
          : childType === 'distractor' || childType === 'correction'
            ? 'correction'
            : parent.section;
    return {
      key,
      Icon,
      count: children.length,
      ariaLabel: ariaLabelFor(children.length),
      expanded: isExpanded(key),
      onToggle: () => toggleExpanded(key),
      onAdd: () => {
        ensureExpanded(key);
        insertBlock(children[children.length - 1] ?? null, childSection, childType, parent.id);
      },
    };
  }

  function childTogglesFor(block: ExerciseBlock): ChildToggle[] {
    const toggles: ChildToggle[] = [];

    if (block.block_type === 'statement_question') {
      const cs = correctionsByParent.get(block.id) ?? [];
      toggles.push(
        makeToggle(
          block,
          'corrections',
          (n) => `${n} corrigé${n > 1 ? 's' : ''}`,
          CheckCircle2,
          cs,
          'correction',
        ),
      );
      // Chasse aux indices enfant : un clue_hunt peut être racine (bloc
      // autonome) OU enfant d'une question (via bouton « Générer chasse IA »).
      // On expose donc la pastille sur la question aussi.
      const ch = clueHuntsByParent.get(block.id) ?? [];
      toggles.push(
        makeToggle(
          block,
          'clue_hunts',
          (n) => `${n} chasse${n > 1 ? 's' : ''} aux indices`,
          Search,
          ch,
          'clue_hunt',
        ),
      );
    }
    if (block.block_type === 'correction' && block.parent_id !== null) {
      const ds = distractorsByParent.get(block.id) ?? [];
      toggles.push(
        makeToggle(
          block,
          'distractors',
          (n) => `${n} distracteur${n > 1 ? 's' : ''}`,
          XCircle,
          ds,
          'distractor',
        ),
      );
    }

    // Indices et explications acceptés sur : statement, statement_question,
    // correction, distractor.
    if (
      block.block_type === 'statement' ||
      block.block_type === 'statement_question' ||
      block.block_type === 'correction' ||
      block.block_type === 'distractor'
    ) {
      const hs = hintsByParent.get(block.id) ?? [];
      const es = explanationsByParent.get(block.id) ?? [];
      toggles.push(
        makeToggle(block, 'hints', (n) => `${n} indice${n > 1 ? 's' : ''}`, Lightbulb, hs, 'hint'),
      );
      toggles.push(
        makeToggle(
          block,
          'explanations',
          (n) => `${n} explication${n > 1 ? 's' : ''}`,
          Info,
          es,
          'explanation',
        ),
      );
    }

    return toggles;
  }

  // Itère récursivement les enfants d'un bloc selon les toggles ouverts.
  function pushChildren(
    parent: ExerciseBlock,
    depth: number,
    out: Array<{ block: ExerciseBlock; depth: number }>,
  ) {
    if (parent.block_type === 'statement_question') {
      if (isExpanded(`${parent.id}:corrections`)) {
        for (const c of correctionsByParent.get(parent.id) ?? []) {
          out.push({ block: c, depth });
          pushChildren(c, depth + 1, out);
        }
      }
    }
    if (parent.block_type === 'correction' && parent.parent_id !== null) {
      if (isExpanded(`${parent.id}:distractors`)) {
        for (const d of distractorsByParent.get(parent.id) ?? []) {
          out.push({ block: d, depth });
          pushChildren(d, depth + 1, out);
        }
      }
    }
    if (
      parent.block_type === 'statement' ||
      parent.block_type === 'statement_question' ||
      parent.block_type === 'correction' ||
      parent.block_type === 'distractor'
    ) {
      if (isExpanded(`${parent.id}:hints`)) {
        for (const h of hintsByParent.get(parent.id) ?? []) {
          out.push({ block: h, depth });
        }
      }
      if (isExpanded(`${parent.id}:explanations`)) {
        for (const e of explanationsByParent.get(parent.id) ?? []) {
          out.push({ block: e, depth });
        }
      }
    }
    if (parent.block_type === 'statement_question') {
      if (isExpanded(`${parent.id}:clue_hunts`)) {
        for (const c of clueHuntsByParent.get(parent.id) ?? []) {
          out.push({ block: c, depth });
        }
      }
    }
  }

  function renderBlockItem(
    block: ExerciseBlock,
    ctx: { isFirst: boolean; isLast: boolean; depth: number },
  ) {
    const isEditing = editingId === block.id;
    const sectionList = blocksInSection(blocks, block.section);
    const sectionIdx = sectionList.findIndex((b) => b.id === block.id);
    const canMergeWithPrev =
      sectionIdx > 0 &&
      sectionList[sectionIdx - 1]!.block_type === block.block_type &&
      sectionList[sectionIdx - 1]!.parent_id === block.parent_id &&
      primaryTextField(block.block_type) !== null;

    const wrapperCls = ctx.depth > 0 ? 'ml-3 sm:ml-6 border-l-2 border-slate-200 pl-2 sm:pl-3' : '';

    return (
      <div key={block.id} className={wrapperCls}>
        {isEditing ? (
          <BlockShell
            block={block}
            isParametric={isParametric}
            questions={questions}
            corrections={corrections}
            isFirst={ctx.isFirst}
            isLast={ctx.isLast}
            isDisabled={isMutating}
            onMoveUp={() => moveBlock(block, 'up')}
            onMoveDown={() => moveBlock(block, 'down')}
            onInsertAfter={(type) => insertBlock(block, block.section, type)}
            onDuplicate={() => duplicateBlock(block)}
            onDelete={() => deleteBlock(block)}
            onPatchData={(data) => patchData(block, data)}
            onPatchParent={(parent_id) => patchParent(block, parent_id)}
            onIsolateVisual={(visualId) => isolateVisual(block, visualId)}
            onSplitRange={(start, end) => splitBlockSelection(block, start, end)}
            onMergeWithPrev={() => mergeWithPrev(block)}
            canMergeWithPrev={canMergeWithPrev}
            onDoneEditing={() => setEditingId(null)}
            exerciseId={exerciseId}
            allBlocks={blocks}
          />
        ) : (
          <BlockCard
            block={block}
            isFirst={ctx.isFirst}
            isLast={ctx.isLast}
            isDisabled={isMutating}
            onMoveUp={() => moveBlock(block, 'up')}
            onMoveDown={() => moveBlock(block, 'down')}
            onEdit={() => setEditingId(block.id)}
            onDuplicate={() => duplicateBlock(block)}
            onDelete={() => deleteBlock(block)}
            childToggles={childTogglesFor(block)}
            lessonLinkCount={lessonLinkCountByBlock.get(block.id) ?? 0}
            voiceTools={
              block.block_type === 'statement_question' ||
              block.block_type === 'correction' ||
              block.block_type === 'distractor' ? (
                <>
                  <BlockVoiceTools exerciseId={exerciseId} block={block} blocks={blocks} />
                  {block.block_type === 'statement_question' && !isParametric && (
                    <QuestionScratchTools
                      exerciseId={exerciseId}
                      question={block}
                      allBlocks={blocks}
                    />
                  )}
                </>
              ) : undefined
            }
            badges={
              block.block_type === 'statement_question' && !isParametric ? (
                <QuestionSheetBadge exerciseId={exerciseId} question={block} allBlocks={blocks} />
              ) : undefined
            }
          />
        )}
      </div>
    );
  }

  // ── Construction du flux unifié ──────────────────────────────────────────

  const unifiedItems: Array<{ block: ExerciseBlock; depth: number }> = [];
  // Section statement : on suit l'ordre des positions (statement + statement_question
  // mélangés). C'est crucial après un split de question : l'énoncé créé conserve sa
  // place visuelle juste avant la question d'origine.
  for (const b of statementBlocks) {
    unifiedItems.push({ block: b, depth: 0 });
    pushChildren(b, 1, unifiedItems);
  }
  for (const c of orphanCorrections) {
    unifiedItems.push({ block: c, depth: 0 });
    pushChildren(c, 1, unifiedItems);
  }
  for (const e of orphanExplanations) {
    unifiedItems.push({ block: e, depth: 0 });
  }

  // Filet de sécurité : rendre tout bloc des sections statement/correction qui
  // n'aurait pas trouvé sa place dans l'arborescence (ex. hint orphelin, parent
  // exotique). Les notes sont rendues à part dans la section meta.
  const seenIds = new Set(unifiedItems.map((it) => it.block.id));
  for (const b of blocks) {
    continue;
    if (b.section === 'meta') continue;
    if (seenIds.has(b.id)) continue;
    unifiedItems.push({ block: b, depth: 0 });
  }

  return (
    <div className="pb-safe mx-auto max-w-4xl space-y-4 px-3 py-6 sm:px-6">
      {/* Barre d'ajout globale */}
      <div className="flex flex-wrap gap-1">
        <Button
          size="sm"
          variant="outline"
          onClick={() => insertBlock(null, 'statement', 'statement')}
          disabled={isMutating}
        >
          + Énoncé / contexte
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => insertBlock(null, 'statement', 'statement_question')}
          disabled={isMutating}
        >
          + Question
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => insertBlock(null, 'correction', 'correction')}
          disabled={isMutating}
        >
          + Étape intermédiaire
        </Button>
      </div>

      {/* Flux unifié */}
      {unifiedItems.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          Exercice vide. Commencez par ajouter un bloc d'énoncé puis des questions.
        </div>
      ) : (
        <ul className="space-y-2">
          {unifiedItems.map((item, idx) => (
            <li key={item.block.id}>
              {renderBlockItem(item.block, {
                isFirst: idx === 0,
                isLast: idx === unifiedItems.length - 1,
                depth: item.depth,
              })}
            </li>
          ))}
        </ul>
      )}

      {/* Notes tuteur (section meta) repliable */}
      <div className="pt-2">
        <button
          onClick={() => setShowMeta((v) => !v)}
          className="min-h-9 text-xs text-slate-500 underline-offset-2 hover:underline"
        >
          {showMeta ? '▼' : '▶'} Notes tuteur ({metaBlocks.length})
        </button>
        {showMeta ? (
          <div className="mt-3 space-y-2">
            <div className="flex flex-wrap gap-1">
              <Button
                size="sm"
                variant="outline"
                onClick={() => insertBlock(null, 'meta', 'note')}
                disabled={isMutating}
              >
                + Note
              </Button>
            </div>
            {metaBlocks.length === 0 ? (
              <p className="rounded-md border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500">
                Aucune note tuteur.
              </p>
            ) : (
              <ul className="space-y-2">
                {metaBlocks.map((b, idx) => (
                  <li key={b.id}>
                    {renderBlockItem(b, {
                      isFirst: idx === 0,
                      isLast: idx === metaBlocks.length - 1,
                      depth: 0,
                    })}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </div>

      {isMutating ? (
        <div className="fixed bottom-4 right-4 rounded-full bg-slate-900 px-3 py-1 text-xs text-white shadow-lg">
          Enregistrement…
        </div>
      ) : null}

      {mutationError ? (
        <div className="fixed bottom-4 left-4 right-4 max-w-2xl rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 shadow-lg sm:left-auto">
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-1">
              <p className="font-medium">Échec de l'enregistrement</p>
              <p className="whitespace-pre-wrap font-mono text-xs">
                {(() => {
                  const e = mutationError as Error & {
                    context?: { body?: string };
                  };
                  return e?.context?.body ?? e?.message ?? String(e);
                })()}
              </p>
            </div>
            <button
              type="button"
              onClick={onResetError}
              className="min-h-9 rounded p-1 text-red-700 hover:bg-red-100"
              aria-label="Fermer l'erreur"
            >
              ✕
            </button>
          </div>
        </div>
      ) : null}

      <p className="text-xs text-slate-400">
        ⓘ Toutes les modifications sont enregistrées automatiquement.
      </p>
      <p className="hidden">{exerciseV2QueryKey(exerciseId).join(',')}</p>
    </div>
  );
}

function primaryTextField(
  t: ExerciseBlockType,
): 'content_md' | 'prompt_md' | 'explanation_md' | 'body_md' | null {
  switch (t) {
    case 'statement':
      return 'content_md';
    case 'statement_question':
      return 'prompt_md';
    case 'correction':
      return 'explanation_md';
    case 'explanation':
    case 'hint':
    case 'note':
      return 'body_md';
    default:
      return null;
  }
}
